import { ReactNode, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDrawerScrollElement } from './DrawerScrollContext';

type VirtualizedListProps<T> = {
    items: T[];
    itemKey: (item: T) => string;
    renderItem: (item: T) => ReactNode;
    estimateSize?: number;
    /**
     * Set to false when this list's panel is hidden but still mounted (e.g.
     * an unopened facet kept alive so its data stays fetched). Multiple
     * `VirtualizedList`s can share one scroll container (unlike desktop,
     * where each dropdown gets its own); leaving an inactive one attached
     * to that shared element corrupts its position tracking, so it's
     * detached (`getScrollElement` returns null) until active again.
     */
    isActive?: boolean;
};

export function VirtualizedList<T>({
    items,
    itemKey,
    renderItem,
    estimateSize = 52,
    isActive = true,
}: VirtualizedListProps<T>) {
    const scrollElement = useDrawerScrollElement();

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => (isActive ? scrollElement : null),
        estimateSize: () => estimateSize,
        getItemKey: (index) => itemKey(items[index]),
        overscan: 8,
    });

    // react-virtual re-applies a cached scroll offset on reattach, so force a reset instead.
    const wasActiveRef = useRef(isActive);

    useEffect(() => {
        if (isActive && !wasActiveRef.current) {
            scrollElement?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }

        wasActiveRef.current = isActive;
    }, [isActive, scrollElement]);

    return (
        <ul style={{ position: 'relative', height: virtualizer.getTotalSize(), width: '100%' }}>
            {virtualizer.getVirtualItems().map((virtualRow) => (
                <li
                    key={String(virtualRow.key)}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                    }}
                >
                    {renderItem(items[virtualRow.index])}
                </li>
            ))}
        </ul>
    );
}
